/**
 * /workspace 对话流式回复
 * POST /volc_chat，后端透传火山 SSE 或类 OpenAI 流式 JSON。
 *
 * 说明：部分 Laravel/网关返回的 Content-Type 不是 text/event-stream，
 * 因此统一按「按行缓冲 + 解析 data: 行 + 兜底 JSON」处理，避免“有数据但界面不增字”。
 */

import { resolveApiUrl } from './apiBaseUrl'

const DEFAULT_PATH = '/volc_chat'
const DEFAULT_MODEL = 'doubao-seed-character-251128'

function getToken() {
  if (typeof localStorage === 'undefined') return ''
  return localStorage.getItem('token') || sessionStorage.getItem('token') || ''
}

/** 从单条 SSE JSON 或类 ChatCompletion chunk 里抠出可展示文本 */
function extractTextFromJson(j) {
  if (!j || typeof j !== 'object') return ''
  const d0 = j?.choices?.[0]?.delta
  const m0 = j?.choices?.[0]?.message
  const piece =
    (typeof j.content === 'string' ? j.content : '') ||
    (typeof j.text === 'string' ? j.text : '') ||
    (typeof j.delta === 'string' ? j.delta : '') ||
    (typeof j.data === 'string' ? j.data : '') ||
    (typeof d0?.content === 'string' ? d0.content : '') ||
    (typeof d0?.reasoning_content === 'string' ? d0.reasoning_content : '') ||
    (typeof m0?.content === 'string' ? m0.content : '') ||
    (typeof j?.message?.content === 'string' ? j.message.content : '') ||
    ''
  return piece
}

function tryEmitParsedJsonLine(line, onDelta) {
  const s = (line || '').trim()
  if (!s || s === '[DONE]') return false
  try {
    const j = JSON.parse(s)
    const piece = extractTextFromJson(j)
    if (piece) {
      onDelta(piece)
      return true
    }
  } catch {
    /* not json */
  }
  return false
}

/**
 * 处理一行：SSE data: 或裸 JSON
 */
function processStreamLine(raw, onDelta) {
  const line = String(raw).replace(/\r$/, '')
  const trimmed = line.trim()
  if (!trimmed) return

  const lower = trimmed.slice(0, 5).toLowerCase()
  if (lower === 'data:' || trimmed.toLowerCase().startsWith('data:')) {
    const data = trimmed.slice(5).trim()
    if (!data || data === '[DONE]') return
    let piece = ''
    try {
      const j = JSON.parse(data)
      piece = extractTextFromJson(j)
    } catch {
      piece = data
    }
    if (piece) onDelta(piece)
    return
  }

  tryEmitParsedJsonLine(trimmed, onDelta)
}

/**
 * 统一流读取：始终按 \n 切行解析，结束时冲刷缓冲区
 */
async function readStreamSmart(reader, decoder, signal, onDelta) {
  let carry = ''
  while (true) {
    if (signal && signal.aborted) throw new Error('aborted')
    const { done, value } = await reader.read()
    if (value && value.length) {
      carry += decoder.decode(value, { stream: !done })
    }
    const parts = carry.split('\n')
    carry = parts.pop() || ''
    for (const raw of parts) {
      processStreamLine(raw, onDelta)
    }
    if (done) break
  }

  if (carry.length) {
    const tail = carry.replace(/\r$/, '').trim()
    if (tail) {
      processStreamLine(tail, onDelta)
      if (!tail.toLowerCase().includes('data:')) {
        tryEmitParsedJsonLine(tail, onDelta)
      }
    }
  }
}

function assistantContentToString(m) {
  const c = m && m.content
  if (typeof c === 'string') return c
  if (Array.isArray(c)) {
    return c
      .filter(p => p && p.type === 'text' && typeof p.text === 'string')
      .map(p => p.text)
      .join('\n')
  }
  return ''
}

/**
 * 将界面中的消息列表转为方舟 / 类 OpenAI Chat 接口格式（支持多模态 user 消息）
 * @param {{ role: string, content?: string, apiContent?: string, attachments?: { kind: string, name?: string, dataUrl?: string }[] }[]} messages
 */
export function buildWorkspaceApiMessages(messages) {
  return (messages || []).map(m => {
    if (m.role === 'assistant') {
      const s = assistantContentToString(m)
      return { role: 'assistant', content: (typeof s === 'string' && s.trim() ? s : ' ') }
    }
    const userDisplay = typeof m.content === 'string' ? m.content.trim() : ''
    const userApiOverride =
      typeof m.apiContent === 'string' ? String(m.apiContent).trim() : ''
    const text = userApiOverride || userDisplay
    const att = m.attachments || []
    const images = att.filter(a => a.kind === 'image' && a.dataUrl)
    const videos = att.filter(a => a.kind === 'video')
    let combined = text
    if (videos.length) {
      const names = videos.map(v => v.name || '未命名').join('、')
      combined += (combined ? '\n\n' : '') + `[用户上传参考视频：${names}]`
    }
    if (!images.length) {
      return { role: 'user', content: combined || ' ' }
    }
    const parts = [
      { type: 'text', text: combined || '请结合下列图片理解。' }
    ]
    for (const img of images) {
      parts.push({ type: 'image_url', image_url: { url: img.dataUrl } })
    }
    return { role: 'user', content: parts }
  })
}

/**
 * @param {Object} opts
 * @param {{ role: string, content?: string, attachments?: object[] }[]} opts.messages
 * @param {AbortSignal} [opts.signal]
 * @param {(chunk: string) => void} opts.onDelta
 */
export async function streamWorkspaceReply(opts) {
  const { messages, signal, onDelta } = opts
  const preset = opts && opts.preset != null ? String(opts.preset) : ''
  const token = getToken()
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream, application/json, text/plain, */*'
  }
  if (token) headers.Authorization = token

  const apiMessages = buildWorkspaceApiMessages(messages)

  let res
  try {
    res = await fetch(resolveApiUrl(DEFAULT_PATH), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model: DEFAULT_MODEL,
        messages: apiMessages,
        stream: true,
        ...(preset ? { preset } : {})
      }),
      signal
    })
  } catch (e) {
    const reason = e?.message ? String(e.message) : 'Network Error'
    throw new Error(`请求失败：${reason}`)
  }

  if (!res.ok) {
    let text = ''
    try {
      text = await res.text()
    } catch (e) {
      text = ''
    }
    const short = (text || '').slice(0, 300)
    throw new Error(`后端返回 ${res.status}：${short || '无响应体'}`)
  }
  if (!res.body) throw new Error('后端无响应流（res.body 为空）')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()

  try {
    await readStreamSmart(reader, decoder, signal, onDelta)
  } catch (e) {
    if (e && e.message === 'aborted') return
    throw e
  }
}
